import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, ListGroup, OverlayTrigger, Popover } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faTag } from '@fortawesome/pro-solid-svg-icons';
import { debounce, isEmpty } from 'lodash';
import { fetchTags, createTag } from 'helpers/clarity_api';

export default function TagPicker({ excludeNames, onTagSelected }) {
  const [tags, setTags] = useState([]);
  const [showOverlay, setShowOverlay] = useState();
  const [searchString, setSearchString] = useState('');

  const debouncedSearch = useCallback(
    debounce((inputString) => {
      if (!inputString) {
        setTags([]);
      } else {
        fetchTags(inputString).then(setTags);
      }
    }, 300),
    []
  );

  const tagSelected = useCallback(
    (tag) => {
      onTagSelected(tag);
      setShowOverlay(false);
    },
    [onTagSelected]
  );

  useEffect(() => {
    debouncedSearch(searchString);
  }, [searchString]);

  const handleCreate = () => {
    createTag(searchString).then(tagSelected);
  };

  const tagsForDisplay = tags.filter(tag => !excludeNames.includes(tag.name));

  const popover = (
    <Popover onClick={e => e.stopPropagation()}>
      <Popover.Header as="h6">Add a tag</Popover.Header>
      <Popover.Body className="px-0 pt-2 pb-0">
        <Form.Group className="mb-2 px-3">
          <Form.Label>Search all tags</Form.Label>

          <div className="with-icon">
            <div className="icon search-icon">
              <FontAwesomeIcon icon={faSearch} />
            </div>

            <Form.Control
              type="text"
              onChange={e => setSearchString(e.target.value)}
            />
          </div>
        </Form.Group>

        <ListGroup style={{ maxHeight: '200px', overflow: 'auto' }}>
          {tagsForDisplay.map(tag => (
            <ListGroup.Item
              action
              className="border-0"
              key={tag.id}
              onClick={() => tagSelected(tag)}
            >
              {tag.name}
            </ListGroup.Item>
          ))}
          {isEmpty(tagsForDisplay) && (
            <ListGroup.Item className="border-0">
              No results found
            </ListGroup.Item>
          )}
          {searchString && (
            <ListGroup.Item
              action
              className="border-0 link-primary"
              onClick={handleCreate}
            >
              + Create new tag ({searchString})
            </ListGroup.Item>
          )}
        </ListGroup>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      overlay={popover}
      placement="bottom"
      rootClose
      show={showOverlay}
      trigger="click"
      onToggle={show => setShowOverlay(show)}
    >
      {isEmpty(excludeNames) ? (
        <Button variant="outline-primary">
          <FontAwesomeIcon icon={faTag} />
          Add Tag
        </Button>
      ) : (
        <div className="clickable">
          <FontAwesomeIcon icon={faPlus} size="12" />
        </div>
      )}
    </OverlayTrigger>
  );
}

TagPicker.propTypes = {
  excludeNames: PropTypes.array.isRequired,
  onTagSelected: PropTypes.func.isRequired,
};
